import { FC, useEffect, useState, useRef, useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Spinner from '@/components/Spinner';
import {
  Bold, Italic, List, ListOrdered, FileText, Cpu, Drama, SlidersHorizontal, Plus,
} from 'lucide-react';
import { useUserContext } from '@/contexts/UserProvider';
import { useGetBotData, useGetLLMSetting, useListPersonas } from '@/hooks/useBot';
import { addPersona, submitPersona } from '@/services/bots';
import { Persona } from '@/types';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Typography from '@tiptap/extension-typography';
import { marked } from 'marked';
import TurndownService from 'turndown';
import {
  SectionCard, StickyActionBar, RadioCard, FieldLabel, ChatbotPreviewPanel, PreviewTabLayout,
} from './shared';

// Configure markdown parser
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Configure HTML to markdown converter
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
});

// Markdown conversion utilities
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';
  try {
    return marked.parse(markdown) as string;
  } catch {
    return markdown;
  }
};

const htmlToMarkdown = (html: string): string => {
  if (!html) return '';
  try {
    return turndownService.turndown(html);
  } catch {
    return html;
  }
};

interface IPersonaProps {
  token: string;
}

interface GptVersion {
  version_id: number;
  version_name: string;
  // GH#1752: friendly provider label ('OpenAI' / 'Claude') supplied by the
  // backend. Customers must never see raw model names.
  provider_display_name?: string;
  selected?: boolean;
  [key: string]: unknown;
}

const DEFAULT_SYSTEM_PROMPT = `You are a helpful AI assistant for MarketBetter.ai.

**Please:**
- Provide accurate and helpful information
- Be professional and friendly in your responses
- Focus on marketing and business-related topics when relevant
- Ask clarifying questions if needed

Always strive to deliver valuable insights and assistance to help users achieve their business goals.`;

// Rich-text toolbar for the System Instruction editor.
const SystemInstructionToolbar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) return null;

  const btn = (active: boolean) =>
    clsx(
      'flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-gray-200',
      active ? 'bg-gray-300 text-gray-900' : 'text-gray-600',
    );

  return (
    <div className="flex flex-wrap items-center gap-1 border-t border-gray-200 bg-gray-50 px-3 py-2">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btn(editor.isActive('bold'))} title="Bold">
        <Bold className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btn(editor.isActive('italic'))} title="Italic">
        <Italic className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btn(editor.isActive('bulletList'))} title="Bullet List">
        <List className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btn(editor.isActive('orderedList'))} title="Numbered List">
        <ListOrdered className="h-4 w-4" />
      </button>
    </div>
  );
};

const PersonaPage: FC<IPersonaProps> = ({ token }) => {
  const { vendorConfig } = useUserContext();
  const { data: botData, isLoading: isBotDataLoading } = useGetBotData(token);
  const { data: getLLM, isLoading: isLLMLoading, refetch: refetchLLMSetting } = useGetLLMSetting(token);
  const { data: fetchedPersonas, isLoading: isPersonasLoading } = useListPersonas(token);

  const [newPersonaTitle, setNewPersonaTitle] = useState('');
  const [newPersonaDescription, setNewPersonaDescription] = useState('');
  const [chatHeight, setChatHeight] = useState<number>(700);
  const [similarity, setSimilarity] = useState<number | undefined>();

  // Live-preview iframe state. The preview reloads after a save (deferred so the
  // persona has time to propagate on the backend) or on manual reset — each bumps
  // previewNonce, which changes demoUrl and re-keys the iframe.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isReadyToRenderIframe, setIsReadyToRenderIframe] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isStrictModeStableRef = useRef(false);

  const demoUrl = useMemo(() => {
    if (!token) return '';
    const base = process.env.NEXT_PUBLIC_CHATBOT_URL;
    const s = typeof base === 'string' ? base : String(base || '');
    if (!s || s.trim() === '' || s === 'undefined') return '';
    const b = s.trim();
    const sep = b.includes('?') ? '&' : '?';
    return `${b}${sep}token=${encodeURIComponent(token)}&defaultOpenBot=true&preview=true&_r=${previewNonce}`;
  }, [token, previewNonce]);
  const [customPrompt, setCustomPrompt] = useState<string>(DEFAULT_SYSTEM_PROMPT);
  const [lowConfidenceMessage, setLowConfidenceMessage] = useState<string>('');
  const [showContactFormOnLowConfidence, setShowContactFormOnLowConfidence] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);

  // Track last value to prevent unnecessary updates
  const lastPromptValue = useRef(customPrompt);
  // Pending preview-reload timer. The persona takes a moment to propagate on the
  // backend before the preview iframe reflects it, so the reload is deferred.
  // Held in a ref so we can cancel it on unmount / bot switch and avoid setting
  // state on a stale component.
  const reloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine if initial data is still loading
  const isInitialLoading = !token || isBotDataLoading || isLLMLoading || isPersonasLoading;

  // TipTap Editor for System Instruction - only initialize after data is ready
  const systemInstructionEditor = useEditor({
    extensions: [
      StarterKit,
      Typography,
      Placeholder.configure({
        placeholder: 'Define how your AI assistant should behave and respond to users...',
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: isDataReady ? markdownToHtml(customPrompt) : '',
    onUpdate: ({ editor }) => {
      // Convert HTML back to markdown for storage
      const htmlContent = editor.getHTML();
      const markdownContent = htmlToMarkdown(htmlContent);

      // Only update if content actually changed to prevent cursor issues
      if (markdownContent !== lastPromptValue.current) {
        lastPromptValue.current = markdownContent;
        setCustomPrompt(markdownContent);
      }
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose focus:outline-none max-w-none py-3 px-4 min-h-[120px] max-h-[400px] overflow-y-auto text-gray-900',
      },
    },
  }, [isDataReady]); // Re-create editor when data becomes ready

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const addedPersona = await addPersona(newPersonaTitle, newPersonaDescription, token);
      if (addedPersona) {
        setPersonas((prevPersonas) => [...prevPersonas, addedPersona]);
        setNewPersonaTitle('');
        setNewPersonaDescription('');
        setPersonaModalOpen(false);
        toast.success('Persona created');
      }
    } catch {
      toast.error('Could not create persona. Please try again.');
    }
  };

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [chatgptModel, setChatGPTModel] = useState<GptVersion[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [selectedModelOption, setSelectModelOption] = useState<number | null>(null);
  const [personaModalOpen, setPersonaModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const init = useCallback(async () => {
    try {
      if (getLLM) {
        setSimilarity(Number(getLLM.similarity_score));
        const prompt = getLLM.custom_system_prompt || DEFAULT_SYSTEM_PROMPT;
        setCustomPrompt(prompt);
        lastPromptValue.current = prompt;
        // Always set, never only-when-truthy: a bot with no low_confidence_config
        // (a fresh bot, or one switched to from a bot that had one) must reset to
        // defaults here, or the previous bot's values keep showing as if they
        // belonged to the newly selected bot.
        setLowConfidenceMessage(getLLM.low_confidence_config?.fallback_message || '');
        setShowContactFormOnLowConfidence(getLLM.low_confidence_config?.show_contact_form || false);
      }
      if (botData?.data?.customizationdata?.chatHeight) {
        setChatHeight(botData.data.customizationdata.chatHeight);
      }
      if (!vendorConfig?.vendorId) {
        throw new Error('Failed to retrieve vendor ID');
      }
      if (fetchedPersonas) {
        setPersonas(fetchedPersonas.personas);
        setChatGPTModel(fetchedPersonas.gpt_version);
        const selectedPersona = fetchedPersonas.personas.find((p: Persona) => p.selected);
        const selectedObjects = fetchedPersonas.gpt_version.filter(
          (obj: GptVersion) => obj.selected === true,
        );
        if (selectedPersona) {
          setSelectedOption(selectedPersona.persona_id);
        }
        // GH#1752: a vendor's saved model may no longer be in the
        // customer-visible set (e.g. it was Gemini/Kimi or a legacy alias).
        // In that case nothing is flagged selected, so default the picker to
        // the first available option (OpenAI). The backend normalizes the
        // stored id on next save, so this stays consistent.
        const visibleModels: GptVersion[] = fetchedPersonas.gpt_version || [];
        if (selectedObjects.length > 0) {
          setSelectModelOption(selectedObjects[0].version_id);
        } else if (visibleModels.length > 0) {
          const openAiDefault = visibleModels.find(
            (m: GptVersion) => m.provider_display_name === 'OpenAI',
          );
          setSelectModelOption((openAiDefault || visibleModels[0]).version_id);
        }
      }
      // Mark data as ready after all state is set - always set true once loading completes
      // This prevents infinite spinner when either API returns empty/null data
      setIsDataReady(true);
    } catch {
      // Still mark as ready on error to prevent infinite spinner - UI will handle gracefully
      setIsDataReady(true);
    }
  }, [getLLM, botData, vendorConfig?.vendorId, token, fetchedPersonas]);

  const onHandleSavePersona = async () => {
    if (selectedOption === null) return;
    try {
      setIsLoading(true);
      // Get the current content from the editor
      const currentPrompt = systemInstructionEditor?.getHTML() || customPrompt;
      const requestedLowConfidence = lowConfidenceMessage.trim()
        ? { fallback_message: lowConfidenceMessage.trim(), show_contact_form: showContactFormOnLowConfidence }
        : null;
      // Save persona, model selection, and advanced settings in one atomic call.
      // submitPersona now rejects on a failed request instead of swallowing it,
      // so a genuine failure lands in the catch below.
      await submitPersona(selectedOption, token, selectedModelOption, {
        similarity_score: similarity,
        custom_system_prompt: currentPrompt,
        low_confidence_config: requestedLowConfidence,
      });

      // A 200 only proves the request was accepted, not that this exact value
      // persisted (QX reported "it always goes back on" — the request can
      // succeed while an unrelated write clobbers it). Re-read the server's
      // own copy and require it to match before calling this a success; the
      // refetch also re-hydrates `getLLM`, which the init() effect below
      // already syncs into local state.
      const { data: verified } = await refetchLLMSetting();
      const persistedShowContactForm = verified?.low_confidence_config?.show_contact_form ?? false;
      const requestedShowContactForm = requestedLowConfidence?.show_contact_form ?? false;
      if (persistedShowContactForm !== requestedShowContactForm) {
        toast.error('Configuration did not save correctly — please try again.');
        return;
      }

      // Defer the preview reload to let the saved persona propagate on the
      // backend. Cancel any previous pending reload first so rapid saves don't
      // stack timers.
      if (reloadTimeoutRef.current) clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = setTimeout(() => {
        setIframeLoading(true);
        setPreviewNonce((n) => n + 1);
        reloadTimeoutRef.current = null;
      }, 5000);

      toast.success('Configuration saved');
    } catch {
      toast.error('Could not save configuration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Re-hydrate every field from the last fetched server data.
  const handleReset = useCallback(() => {
    void init();
  }, [init]);

  const handleResetPreview = useCallback(() => {
    setIframeLoading(true);
    setPreviewNonce((n) => n + 1);
  }, []);

  // Mark Strict Mode cycle complete after a tick so the iframe only renders once.
  useEffect(() => {
    if (!isStrictModeStableRef.current) {
      isStrictModeStableRef.current = true;
      setTimeout(() => setIsReadyToRenderIframe(true), 0);
    }
  }, []);

  // Show the loading overlay whenever the preview URL changes.
  useEffect(() => {
    if (demoUrl && demoUrl.trim() !== '') setIframeLoading(true);
  }, [demoUrl]);

  // Reset data ready state when token changes (user switches bots), and cancel
  // any pending preview reload from the previous bot.
  useEffect(() => {
    setIsDataReady(false);
    return () => {
      if (reloadTimeoutRef.current) clearTimeout(reloadTimeoutRef.current);
    };
  }, [token]);

  // Initialize data when all dependencies are ready
  useEffect(() => {
    if (token && !isInitialLoading) {
      init();
    }
  }, [token, vendorConfig?.vendorId, fetchedPersonas, botData, getLLM, isInitialLoading, init]);

  // Sync editor content when customPrompt changes externally (not from user typing)
  useEffect(() => {
    if (systemInstructionEditor && isDataReady && customPrompt !== lastPromptValue.current && !systemInstructionEditor.isFocused) {
      const htmlContent = markdownToHtml(customPrompt);
      lastPromptValue.current = customPrompt;
      systemInstructionEditor.commands.setContent(htmlContent, false);
    }
  }, [customPrompt, systemInstructionEditor, isDataReady]);

  // Show loading state until data is ready
  if (isInitialLoading || !isDataReady) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner width={32} height={32} />
          <p className="text-sm text-gray-500">Loading LLM configuration...</p>
        </div>
      </div>
    );
  }

  const shouldRenderIframe = !!(
    demoUrl && demoUrl.trim() !== '' && isReadyToRenderIframe && botData?.data
  );

  return (
    <>
      <PreviewTabLayout
        preview={(
          <ChatbotPreviewPanel
            demoUrl={demoUrl}
            shouldRenderIframe={shouldRenderIframe}
            iframeLoading={iframeLoading}
            chatHeight={chatHeight}
            iframeRef={iframeRef}
            onReset={handleResetPreview}
            onIframeSettled={() => setIframeLoading(false)}
            debug={{
              isReadyToRenderIframe,
              isFetching: isBotDataLoading,
              hasData: !!botData?.data,
              hasToken: !!token,
              vendorId: vendorConfig?.vendorId,
            }}
          />
        )}
        footer={(
          <StickyActionBar
            onReset={handleReset}
            onSave={() => { void onHandleSavePersona(); }}
            saving={isLoading}
            saveDisabled={selectedOption === null}
            resetDisabled={isLoading}
            saveLabel="Save Configuration"
            leftSlot={(
              <p className="text-[13px] text-gray-500">Changes apply to your chatbot immediately after saving.</p>
            )}
          />
        )}
      >
        <div className="space-y-6">
            {/* System instruction */}
            <SectionCard
              icon={FileText}
              title="System Instruction"
              subtitle="Guides how your AI assistant responds to every visitor."
            >
              <div className="overflow-hidden rounded-lg border border-gray-300 bg-white transition-colors focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500">
                <EditorContent
                  editor={systemInstructionEditor}
                  className="max-h-[400px] min-h-[120px] overflow-y-auto focus-within:outline-none"
                />
                {systemInstructionEditor && <SystemInstructionToolbar editor={systemInstructionEditor} />}
              </div>
            </SectionCard>

            {/* AI Model */}
            <SectionCard
              icon={Cpu}
              title="AI Model"
              subtitle="The model that powers your assistant's responses."
            >
              <div role="radiogroup" aria-label="AI model" className="space-y-2">
                {chatgptModel?.map((data: GptVersion) => {
                  // GH#1752: only ever show the friendly provider label. Fall back to
                  // coarse detection only if the backend did not supply it (defensive).
                  const providerLabel = data.provider_display_name
                    || (data.version_name.toLowerCase().includes('claude') ? 'Claude' : 'OpenAI');
                  const isClaude = providerLabel === 'Claude';
                  const isSelected = selectedModelOption === data.version_id;
                  return (
                    <RadioCard
                      key={data.version_id}
                      checked={isSelected}
                      onSelect={() => setSelectModelOption(data.version_id)}
                      title={providerLabel}
                      subtitle={isClaude ? 'Superior analysis and writing' : 'Advanced reasoning and creativity'}
                      trailing={isSelected ? <Badge variant="info" size="xs" className="bg-blue-100 text-blue-700">Active</Badge> : undefined}
                    />
                  );
                })}
              </div>
            </SectionCard>

            {/* Response style / persona */}
            <SectionCard
              icon={Drama}
              title="Response Style"
              subtitle="The persona that shapes your assistant's tone and approach."
            >
              <div role="radiogroup" aria-label="Response style" className="space-y-2">
                {personas?.map((persona: Persona) => {
                  const isSelected = selectedOption === persona.persona_id;
                  return (
                    <RadioCard
                      key={persona.persona_id}
                      checked={isSelected}
                      onSelect={() => setSelectedOption(persona.persona_id)}
                      title={persona.name}
                      subtitle={persona.description}
                      trailing={isSelected ? <Badge variant="info" size="xs" className="bg-blue-100 text-blue-700">Active</Badge> : undefined}
                    />
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => setPersonaModalOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:border-primary/40 hover:bg-primary/[0.04] hover:text-primary"
              >
                <Plus className="h-4 w-4" /> Add custom persona
              </button>
            </SectionCard>

            {/* Advanced settings */}
            <SectionCard
              icon={SlidersHorizontal}
              title="Advanced"
              subtitle="Fine-tune retrieval strictness and low-confidence behavior."
            >
              {/* Similarity threshold */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <FieldLabel hint="Controls how closely responses must match your training data.">
                    Similarity threshold
                  </FieldLabel>
                  <span className="min-w-[48px] rounded-md border border-gray-300 px-2 py-1 text-center text-sm font-medium text-gray-700">
                    {similarity}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1.0"
                  step="0.1"
                  value={similarity}
                  onChange={(e) => setSimilarity(parseFloat(e.target.value))}
                  className="slider w-full"
                  aria-label="Similarity threshold"
                />
                <div className="mt-1 flex justify-between text-xs text-gray-500">
                  <span>Flexible</span>
                  <span>Moderate</span>
                  <span>Strict</span>
                </div>
              </div>

              {/* Low confidence response */}
              <div className="border-t border-gray-100 pt-5">
                <FieldLabel hint="Used when the AI isn't confident it has the right answer. Leave empty for default behavior.">
                  Low confidence response
                </FieldLabel>
                <Textarea
                  value={lowConfidenceMessage}
                  onChange={(e) => setLowConfidenceMessage(e.target.value)}
                  placeholder='e.g. "Thank you for your interest! Unfortunately, I do not have information about that topic. Please visit our contact page for additional help."'
                  rows={3}
                  className="resize-y"
                />
                <label className="mt-3 flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={showContactFormOnLowConfidence}
                    onCheckedChange={(checked) => setShowContactFormOnLowConfidence(checked === true)}
                  />
                  <span className="text-sm text-gray-700">Show contact form when confidence is low</span>
                </label>
              </div>
            </SectionCard>
        </div>
      </PreviewTabLayout>

      {/* Create persona modal */}
      <Dialog open={personaModalOpen} onOpenChange={setPersonaModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create custom persona</DialogTitle>
            <DialogDescription>Define a new response style for your AI assistant.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <FieldLabel>Persona name</FieldLabel>
              <Input
                placeholder="e.g. Professional Consultant, Friendly Helper..."
                value={newPersonaTitle}
                onChange={(e) => setNewPersonaTitle(e.target.value)}
                required
              />
            </div>
            <div>
              <FieldLabel hint="Be specific about tone, style, and approach.">Description</FieldLabel>
              <Textarea
                rows={4}
                placeholder="Describe how this persona should respond to users..."
                value={newPersonaDescription}
                onChange={(e) => setNewPersonaDescription(e.target.value)}
                className="resize-y"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPersonaModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newPersonaTitle.trim() || !newPersonaDescription.trim()}>
                Create persona
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PersonaPage;
